import { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GitMerge, GitBranch, Loader2, AlertCircle, Play, CheckCircle2, RotateCcw, Clock } from 'lucide-react';
import { reposApi, workflowRunsApi } from '../api/client';
import SearchableSelect from '../components/SearchableSelect';
import { StatusDot, StatusLabel, StatusIcon, formatDuration } from '../components/DeployStatus';
import WorkflowHistoryPanel from '../components/WorkflowHistoryPanel';

const NODE_W = 360;
const NODE_H = 160;
const COL_WIDTH = NODE_W + 48;
const WAVE_HEIGHT = NODE_H + 90;
const PAD = 32;
const RETRY_BTN_SIZE = 32;
const FAILURE_STATUSES = new Set(['failed', 'timeout', 'cancelled', 'expired']);

// Reorders nodes within each wave so that chains of single dependencies line
// up in the same column (straight vertical connector) instead of landing in
// whatever column order the API happened to return. Standard layered-graph
// barycenter heuristic: repeatedly reorder each wave by the average column
// of its neighbors in the adjacent wave, alternating direction so info
// propagates both down the chain and back up to its root.
function computeWaveOrder(graph) {
  const waveIds = graph.waves.map(w => w.repos.map(r => r.id));
  const predecessors = {};
  const successors = {};
  graph.nodes.forEach(n => { predecessors[n.id] = []; successors[n.id] = []; });
  graph.edges.forEach(e => {
    predecessors[e.to]?.push(e.from);
    successors[e.from]?.push(e.to);
  });

  const order = waveIds.map(ids => [...ids]);
  const colIndex = {};
  const syncColIndex = () => order.forEach(w => w.forEach((id, i) => { colIndex[id] = i; }));
  syncColIndex();

  function reorderWave(waveIdx, neighborsOf) {
    const scored = order[waveIdx].map((id, i) => {
      const cols = neighborsOf(id).map(n => colIndex[n]).filter(c => c !== undefined);
      const bary = cols.length ? cols.reduce((a, b) => a + b, 0) / cols.length : Infinity;
      return { id, bary, i };
    });
    scored.sort((a, b) => a.bary - b.bary || a.i - b.i);
    order[waveIdx] = scored.map(s => s.id);
    syncColIndex();
  }

  for (let pass = 0; pass < 4; pass++) {
    if (pass % 2 === 0) {
      for (let w = 1; w < order.length; w++) reorderWave(w, id => predecessors[id]);
    } else {
      for (let w = order.length - 2; w >= 0; w--) reorderWave(w, id => successors[id]);
    }
  }

  const repoById = Object.fromEntries(graph.nodes.map(n => [n.id, n]));
  return order.map(ids => ids.map(id => repoById[id]));
}

// Standalone page (opened in its own browser window) showing the deployment
// dependency graph as vertical waves — wave 1 at top, later waves below, with
// parallel repos in the same wave laid out side by side. Clicking a node lets
// you configure which Cloud Build trigger "Deploy All" uses for that repo,
// and "Deploy All" here runs wave by wave with live status shown right on
// each node (no separate modal).
export default function WorkflowView() {
  const location = useLocation();

  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [triggersMap, setTriggersMap] = useState({});
  const [triggersLoading, setTriggersLoading] = useState(true);
  const [triggerErrors, setTriggerErrors] = useState({});
  const [draftTriggers, setDraftTriggers] = useState({});

  const [branch, setBranch] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [currentWave, setCurrentWave] = useState(null);
  const [buildStates, setBuildStates] = useState({});
  const [deployError, setDeployError] = useState(null);
  const [done, setDone] = useState(false);
  const [runId, setRunId] = useState(null);
  const [retryingRepoId, setRetryingRepoId] = useState(null);
  const pollRef = useRef(null);
  const autoDeployTried = useRef(false);
  const activeRunPromiseRef = useRef(null);
  const draftTriggersPromiseRef = useRef(null);

  useEffect(() => {
    reposApi.dependencyGraph()
      .then(setGraph)
      .catch(e => setError(e.error || e.message || 'Failed to load dependency graph'))
      .finally(() => setLoading(false));

    reposApi.listGcpTriggers()
      .then(triggers => {
        const map = {};
        triggers.forEach(t => {
          if (!t.repoName) return;
          if (!map[t.repoName]) map[t.repoName] = [];
          map[t.repoName].push(t);
        });
        setTriggersMap(map);
      })
      .catch(() => setTriggersMap({}))
      .finally(() => setTriggersLoading(false));

    // The single always-present draft run is what the trigger-picker dropdowns
    // read/write — it's the persistent "next deploy" template.
    draftTriggersPromiseRef.current = workflowRunsApi.draft()
      .then(draft => {
        const dt = {};
        draft.wavePlan.forEach(wave => wave.repos.forEach(r => { dt[r.repoId] = r.deployTriggerName || ''; }));
        setDraftTriggers(dt);
        return dt;
      })
      .catch(() => ({}));
  }, []);

  // Rehydrate an in-progress or failed run on mount/refresh — the backend
  // orchestrates waves and persists status, so a refresh only needs to
  // re-fetch and resume polling, never restart the deployment. A run that
  // already finished successfully is done being interesting — refreshing
  // after a full success should just show the (now-idle) draft again rather
  // than re-displaying stale success statuses.
  useEffect(() => {
    activeRunPromiseRef.current = workflowRunsApi.latest()
      .then(({ run }) => { if (run && run.status !== 'success') startPolling(run); return run; })
      .catch(() => null);
  }, []); // eslint-disable-line

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const allTriggersConfigured = !!graph && graph.waves.length > 0
    && graph.waves.every(w => w.repos.every(r => !!draftTriggers[r.id]));

  // Applies a run fetched from the backend onto local UI state. The backend
  // owns wave-triggering and Cloud Build polling now, so this is the only
  // place that turns a run's wavePlan snapshot into what the nodes render.
  function applyRun(run) {
    setRunId(run.id);
    setBranch(run.branch);
    setCurrentWave(run.currentWave);
    const bs = {};
    run.wavePlan.forEach(wave => wave.repos.forEach(r => {
      bs[r.repoId] = {
        status: r.status, buildId: r.buildId, error: r.error, logUrl: r.logUrl,
        startedAt: r.startedAt, finishedAt: r.finishedAt,
      };
    }));
    setBuildStates(bs);
    if (run.status === 'running') {
      setDeploying(true); setDone(false); setDeployError(null);
    } else if (run.status === 'success') {
      setDeploying(false); setDone(true); setDeployError(null);
    } else {
      setDeploying(false); setDone(false); setDeployError(run.error || 'Deployment failed.');
    }
  }

  // Polls the backend for run status (rather than Cloud Build directly) so
  // progress survives refreshes — the backend keeps driving waves even if
  // no one is watching.
  function startPolling(run) {
    applyRun(run);
    if (run.status !== 'running') return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { run: latest } = await workflowRunsApi.latest();
        if (!latest) return;
        applyRun(latest);
        if (latest.status !== 'running') {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        // transient — retry next tick
      }
    }, 5000);
  }

  async function handleDeploy(branchOverride) {
    const b = branchOverride ?? branch;
    setDeploying(true);
    setDeployError(null);
    setDone(false);
    setBuildStates({});
    setCurrentWave(0);
    try {
      const run = await workflowRunsApi.create(b);
      startPolling(run);
    } catch (e) {
      if (e.run) {
        // Another run is already active (e.g. double-click / another tab) — adopt it.
        startPolling(e.run);
      } else {
        setDeployError(e.error || e.message || 'Failed to start deployment');
        setDeploying(false);
      }
    }
  }

  async function handleRetryRepo(repoId) {
    if (!runId || retryingRepoId) return;
    setRetryingRepoId(repoId);
    try {
      const run = await workflowRunsApi.retryRepo(runId, repoId);
      startPolling(run);
    } catch (e) {
      setDeployError(e.error || e.message || 'Failed to retry');
    } finally {
      setRetryingRepoId(null);
    }
  }

  // Arrived here from the Triggers page's "Deploy All" button: carry the
  // branch over and, if every repo already has a trigger configured and no
  // run is already active, start one so status shows live on the nodes below.
  useEffect(() => {
    if (autoDeployTried.current || loading || !graph) return;
    autoDeployTried.current = true;
    const navBranch = location.state?.branch;
    if (navBranch) setBranch(navBranch);
    Promise.all([
      activeRunPromiseRef.current || Promise.resolve(null),
      draftTriggersPromiseRef.current || Promise.resolve({}),
    ]).then(([existingRun, dt]) => {
      if (existingRun && existingRun.status === 'running') return; // already adopted by the rehydration effect
      if (location.state?.autoDeploy && navBranch && graph.waves.length > 0
        && graph.waves.every(w => w.repos.every(r => !!dt[r.id]))) {
        handleDeploy(navBranch);
      }
    });
  }, [loading, graph]); // eslint-disable-line

  function handleTriggerChange(repo, triggerName) {
    setTriggerErrors(prev => ({ ...prev, [repo.id]: null }));
    setDraftTriggers(prev => ({ ...prev, [repo.id]: triggerName }));
    workflowRunsApi.setDraftTrigger(repo.id, triggerName)
      .catch(e => setTriggerErrors(prev => ({ ...prev, [repo.id]: e.error || e.message || 'Failed to save' })));
  }

  let positions = {};
  let svgWidth = 0;
  let svgHeight = 0;
  let orderedWaves = [];
  if (graph) {
    orderedWaves = computeWaveOrder(graph);
    orderedWaves.forEach((repos, wi) => {
      repos.forEach((r, ri) => {
        positions[r.id] = {
          x: PAD + ri * COL_WIDTH,
          y: PAD + wi * WAVE_HEIGHT,
        };
      });
    });
    const maxCols = Math.max(1, ...orderedWaves.map(repos => repos.length));
    svgWidth = PAD * 2 + maxCols * COL_WIDTH - (COL_WIDTH - NODE_W);
    svgHeight = PAD * 2 + Math.max(1, orderedWaves.length) * WAVE_HEIGHT - (WAVE_HEIGHT - NODE_H);
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-white">
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 shrink-0 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-accent-purple/10 flex items-center justify-center shrink-0">
            <GitMerge size={16} className="text-accent-purple" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Deployment Workflow</p>
            <p className="text-xs text-slate-500 truncate">Topological order derived from repo dependencies — pick each repo's Deploy All trigger below it</p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <input
            className="input text-xs py-1.5 px-3 w-40"
            placeholder="Branch to deploy"
            value={branch}
            onChange={e => setBranch(e.target.value)}
            disabled={deploying}
          />
          <button
            onClick={() => handleDeploy()}
            disabled={deploying || !branch || !graph || graph.waves.length === 0 || !allTriggersConfigured}
            title={
              !branch ? 'Enter a branch first'
              : !allTriggersConfigured ? 'Configure a deploy trigger for every repo first'
              : 'Deploy all repos'
            }
            className="btn-primary py-1.5 px-3 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {deploying
              ? <><Loader2 size={12} className="animate-spin" /> Wave {(currentWave ?? -1) + 1} deploying…</>
              : <><Play size={12} /> Deploy All</>
            }
          </button>
          <button
            onClick={() => setHistoryOpen(true)}
            title="Deployment history"
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 shrink-0"
          >
            <Clock size={14} />
          </button>
        </div>
      </div>

      {!loading && !error && graph && !allTriggersConfigured && !deploying && (
        <div className="flex items-center gap-2 bg-amber-50 border-b border-amber-200 px-5 py-2 shrink-0">
          <AlertCircle size={12} className="text-amber-600 shrink-0" />
          <span className="text-xs text-amber-700">Some repos are missing a deploy trigger — pick one from the dropdown on each node below.</span>
        </div>
      )}
      {deployError && (
        <div className="flex items-center gap-2 bg-red-50 border-b border-red-200 px-5 py-2 shrink-0">
          <AlertCircle size={12} className="text-red-500 shrink-0" />
          <span className="text-xs text-red-600">{deployError}</span>
        </div>
      )}
      {done && (
        <div className="flex items-center gap-2 bg-green-50 border-b border-green-200 px-5 py-2 shrink-0">
          <CheckCircle2 size={12} className="text-green-600 shrink-0" />
          <span className="text-xs text-green-700 font-medium">All repos deployed successfully.</span>
        </div>
      )}

      <div
        className="relative flex-1 overflow-auto flex justify-center"
        style={{
          backgroundImage: 'radial-gradient(#e2e8f0 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      >
        {loading && (
          <div className="flex items-center gap-2 text-xs text-slate-500 px-5 py-5">
            <Loader2 size={12} className="animate-spin" /> Loading dependency graph…
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 m-5">
            <AlertCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
            <span className="text-xs text-red-400">{error}</span>
          </div>
        )}
        {graph && graph.nodes.length === 0 && (
          <p className="text-xs text-slate-500 px-5 py-5">No repos configured yet.</p>
        )}

        {graph && graph.nodes.length > 0 && (
          <div style={{ padding: 20 }}>
            <div
              className="relative"
              style={{
                width: svgWidth,
                height: svgHeight,
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
              }}
            >
              <svg width={svgWidth} height={svgHeight} className="absolute inset-0 pointer-events-none overflow-visible">
                {graph.edges.map((e, i) => {
                  const from = positions[e.from];
                  const to = positions[e.to];
                  if (!from || !to) return null;
                  const x1 = from.x + NODE_W / 2;
                  const y1 = from.y + NODE_H;
                  const x2 = to.x + NODE_W / 2;
                  const y2 = to.y;
                  const midY = y1 + (y2 - y1) / 2;
                  const d = x1 === x2
                    ? `M ${x1} ${y1} V ${y2}`
                    : `M ${x1} ${y1} V ${midY} H ${x2} V ${y2}`;
                  return (
                    <g key={i}>
                      <path d={d} fill="none" stroke="#cbd5e1" strokeWidth="1.75" markerEnd="url(#arrow)" />
                      <circle cx={x1} cy={y1} r="2.5" fill="#94a3b8" />
                    </g>
                  );
                })}
                <defs>
                  <marker id="arrow" markerWidth="9" markerHeight="9" refX="7" refY="4" orient="auto">
                    <path d="M0,0 L8,4 L0,8 Z" fill="#94a3b8" />
                  </marker>
                </defs>
              </svg>

              {graph.waves.map((w, wi) =>
                w.repos.map((r) => {
                  const pos = positions[r.id];
                  const bs = buildStates[r.id];
                  const canRetry = bs && FAILURE_STATUSES.has(bs.status) && !!runId;
                  return (
                    <div key={r.id}>
                      <div
                        className={`absolute rounded-2xl border bg-white shadow-sm flex flex-col transition-all ${
                          deploying && currentWave === wi ? 'border-accent-purple/40 ring-2 ring-accent-purple/20' : 'border-slate-200'
                        }`}
                        style={{ left: pos.x, top: pos.y, width: NODE_W, height: NODE_H }}
                      >
                        {bs && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 rounded-full ring-[3px] ring-white">
                            <StatusIcon status={bs.status} size={24} />
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/60 rounded-t-2xl">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                              <GitBranch size={14} className="text-blue-600" />
                            </div>
                            <span className="text-base font-semibold text-slate-800 leading-tight truncate">{r.name}</span>
                          </div>
                          <span className="text-[10px] font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full shrink-0">
                            Wave {wi + 1}
                          </span>
                        </div>

                        <div className="flex-1 flex flex-col justify-center gap-2 px-4 py-3 min-w-0 rounded-b-2xl">
                          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Choose the right trigger for this repo.</span>
                          <SearchableSelect
                            options={(triggersMap[r.name] || []).map(t => t.name)}
                            value={draftTriggers[r.id] || ''}
                            onChange={name => handleTriggerChange(r, name)}
                            placeholder="Choose trigger to deploy"
                            loading={triggersLoading}
                            loadingLabel="Loading triggers…"
                            noMatchLabel="No triggers match."
                            disabled={deploying}
                          />
                          {triggerErrors[r.id] && (
                            <p className="text-[10px] text-red-500 truncate">{triggerErrors[r.id]}</p>
                          )}
                          {bs && (
                            <div className="flex items-center gap-2 min-w-0 pt-0.5">
                              <StatusDot status={bs.status} />
                              <StatusLabel status={bs.status} />
                              {formatDuration(bs.startedAt, bs.finishedAt) && (
                                <span className="text-[10px] font-mono text-slate-400 shrink-0">
                                  {formatDuration(bs.startedAt, bs.finishedAt)}
                                </span>
                              )}
                              {bs.error ? (
                                <span className="text-[10px] text-red-500 truncate">{bs.error}</span>
                              ) : bs.logUrl ? (
                                <a
                                  href={bs.logUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-auto text-[10px] font-mono text-blue-500 hover:text-blue-600 underline underline-offset-2 shrink-0"
                                >
                                  logs
                                </a>
                              ) : bs.buildId ? (
                                <Link
                                  to={`/builds/${bs.buildId}`}
                                  className="ml-auto text-[10px] font-mono text-blue-500 hover:text-blue-600 underline underline-offset-2 shrink-0"
                                >
                                  logs
                                </Link>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>

                      {canRetry && (
                        <button
                          onClick={() => handleRetryRepo(r.id)}
                          disabled={retryingRepoId === r.id}
                          title={`Retry ${r.name}`}
                          className="absolute rounded-full border border-red-200 bg-white shadow-sm flex items-center justify-center text-red-500 hover:bg-red-50 hover:border-red-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          style={{
                            left: pos.x + NODE_W + 8,
                            top: pos.y + NODE_H / 2 - RETRY_BTN_SIZE / 2,
                            width: RETRY_BTN_SIZE,
                            height: RETRY_BTN_SIZE,
                          }}
                        >
                          {retryingRepoId === r.id
                            ? <Loader2 size={14} className="animate-spin" />
                            : <RotateCcw size={14} />
                          }
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {graph && graph.nodes.length > 0 && (
          <div className="absolute bottom-4 left-4 flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
            <button
              onClick={() => setZoom(z => Math.min(1.5, +(z + 0.1).toFixed(2)))}
              className="w-7 h-7 flex items-center justify-center text-slate-500 hover:bg-slate-50 border-b border-slate-200"
            >
              +
            </button>
            <button
              onClick={() => setZoom(z => Math.max(0.5, +(z - 0.1).toFixed(2)))}
              className="w-7 h-7 flex items-center justify-center text-slate-500 hover:bg-slate-50"
            >
              −
            </button>
          </div>
        )}
      </div>

      <WorkflowHistoryPanel open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </div>
  );
}
